import { useIsImproveMpPerformanceTier2Enabled } from '@hooks/use-improve-mp-performance-tier-2';
import { useTrackOpenFilterMenu } from '@hooks/use-track-open-filter-menu';
import { useAppSelector } from '@redux/hooks';
import React, {
	type FunctionComponent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { afterNextPaint } from '../../../../helpers/after-next-paint';
import useClickListenerOnPillDropdown from '../../../pages/VendorsSearch/containers/FilterPills/hooks/useClickListenerOnPillDropdown';
import Dropdown from './Dropdown';
import Pill from './Pill';

interface Option {
	name: string;
	value: string;
	subtext?: string;
}
export interface Props {
	appliedOptions?: string[];
	isMobile: boolean;
	onClearOptions?: () => void;
	onDropdownToggle?: (isOpen: boolean) => void;
	onPillClick?: () => void;
	onSave?: (selectedOptions: string[]) => void;
	options: Option[];
	pillLabel: string;
	singleSelect?: boolean;
	defaultOptions?: string[];
}
const buildPillLabel = (
	pillLabel: string,
	selectedOptionsLength: number,
): string =>
	selectedOptionsLength > 1
		? `${pillLabel} (${selectedOptionsLength})`
		: pillLabel;
const selectionsHaveChanged = (
	appliedOptions: string[],
	selectedOptions: string[],
) =>
	appliedOptions.length !== selectedOptions.length ||
	!appliedOptions.every((option) => selectedOptions.includes(option));
// eslint-disable-next-line @typescript-eslint/no-empty-function
const baseFunction = () => {};
const DEFAULT_ALL_RADIO_OPTION_ID = 'DEFAULT_ALL_RADIO_OPTION_ID';
const PillWithDropdown: FunctionComponent<Props> = ({
	appliedOptions = [],
	isMobile,
	onClearOptions,
	onDropdownToggle = baseFunction,
	onPillClick = baseFunction,
	onSave,
	options,
	pillLabel,
	singleSelect,
	defaultOptions,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [selectedOptions, setSelectedOptions] = useState(appliedOptions || []);
	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
	const isSelected = useMemo(() => {
		return (
			dropdownOpen ||
			appliedOptions.filter((o) => o !== DEFAULT_ALL_RADIO_OPTION_ID).length > 0
		);
	}, [dropdownOpen, appliedOptions]);
	const vendorCategoryCode = useAppSelector((state) => state.category.code);
	const displayMarketCode = useAppSelector(
		(state) => state.location.marketCode,
	);
	const isImproveMpPerformanceTier2Enabled =
		useIsImproveMpPerformanceTier2Enabled();

	const closeDropdown = () => {
		setDropdownOpen(false);
		onDropdownToggle(false);
	};
	useClickListenerOnPillDropdown({
		closeDropdown,
		containerRef,
		dropdownOpen,
		isMobile,
	});
	const trackOpenFilterMenu = useTrackOpenFilterMenu();

	useEffect(() => {
		if (selectionsHaveChanged(appliedOptions, selectedOptions)) {
			setSelectedOptions(appliedOptions);
		}
	}, [appliedOptions, selectedOptions]);

	const onDropdownSave = (updatedOptions: string[]) => {
		afterNextPaint(() => {
			if (isImproveMpPerformanceTier2Enabled) {
				closeDropdown();
				// must share onSave's batch, or the appliedOptions sync effect reverts it before the filter update commits
				unstable_batchedUpdates(() => {
					setSelectedOptions(updatedOptions);
					onSave?.(updatedOptions);
				});
				return;
			}
			setSelectedOptions(updatedOptions);
			closeDropdown();
			if (onSave) {
				onSave(updatedOptions);
			}
		});
	};
	const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const nextDropdownToggle = !dropdownOpen;

		afterNextPaint(() => {
			if (nextDropdownToggle) {
				trackOpenFilterMenu(pillLabel, {
					vendorCategoryCode,
					displayMarketCode,
				});
			}
			onDropdownToggle(nextDropdownToggle);
			setDropdownOpen(nextDropdownToggle);
			onPillClick();
		});
	};
	return (
		<div ref={containerRef}>
			<Pill
				label={buildPillLabel(pillLabel, selectedOptions.length)}
				onClick={onClick}
				selected={isSelected}
				isImproveMpPerformanceTier2Enabled={isImproveMpPerformanceTier2Enabled}
			/>
			{dropdownOpen && (
				<Dropdown
					isModalOpen={dropdownOpen}
					modalHeader={pillLabel}
					onClear={onClearOptions}
					onClose={closeDropdown}
					onSave={onDropdownSave}
					options={options}
					selectedOptions={selectedOptions}
					singleSelect={singleSelect}
					defaultOptions={defaultOptions}
				/>
			)}
		</div>
	);
};
export default PillWithDropdown;
